import React, { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useTokens } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio } from '../utils/luminance';

const primaryCtaDef: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const secondaryCtaDef: SlotGroupDefinition = {
  label: 'Secondary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function ThankYouContent() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const primaryCta = useSlotGroup('thankyoucontent.primary_cta', primaryCtaDef);
  const secondaryCta = useSlotGroup('thankyoucontent.secondary_cta', secondaryCtaDef);

  // Match the canonical "outline CTA fills on hover" treatment used on the
  // Resident Access page: luminance-pick the accent so pale primaries fall
  // back to the dark text token and stay legible against the page surface.
  const tokens = useTokens();
  const [ctaAccent, ctaOnAccent] = useMemo(() => {
    const primary = tokens?.color?.primary;
    const background = tokens?.color?.background || '#ffffff';
    if (!primary) return ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const;
    return contrastRatio(primary, background) >= 4.5
      ? ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const
      : ['var(--vibe-color-text)', 'var(--vibe-color-background)'] as const;
  }, [tokens]);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-16 md:py-24 bg-[var(--vibe-color-background)]"
      style={{
        ['--oasis-ty-cta-accent' as string]: ctaAccent,
        ['--oasis-ty-cta-on-accent' as string]: ctaOnAccent,
      }}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Page title — replaces the removed HeroSolidBG section above so
            this page still leads with a clear "Thank You" heading. Center
            alignment is inherited from the parent container's
            `text-center`. Color uses the secondary token to match the
            hero-style heading treatment used elsewhere on the theme. */}
        <RichTextContent
          path="thankyoucontent.title"
          label="Title"
          defaultValue="<p>Thank You</p>"
          as="h1"
          className="font-heading text-3xl sm:text-4xl md:text-5xl leading-tight mb-6 text-[var(--vibe-color-secondary)]"
        />
        <RichTextContent
          path="thankyoucontent.body"
          label="Body Text"
          defaultValue="<p>Thank you for your interest! A team member will reach out to you soon to help you find your dream home.</p>"
          as="div"
          className="font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
        />

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          <EditableSlotGroup groupPath="thankyoucontent.primary_cta" definition={primaryCtaDef}>
            <a
              href={primaryCta.url || '/floor-plans'}
              target={primaryCta._target || '_self'}
              className="ty-btn"
            >
              <span className="ty-btn-label">{primaryCta.label || 'View Floor Plans'}</span>
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="thankyoucontent.secondary_cta" definition={secondaryCtaDef}>
            <a
              href={secondaryCta.url || '/schedule-a-tour'}
              target={secondaryCta._target || '_self'}
              className="ty-btn"
            >
              <span className="ty-btn-label">{secondaryCta.label || 'Schedule a Tour'}</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Outline CTA matching the canonical "over white" treatment used on
           the Resident Access page: transparent body, accent border + label,
           fills with the accent on hover. The accent is luminance-picked
           (--oasis-ty-cta-accent) so pale primaries fall back to text. */
        .ty-btn {
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          padding: 0.875rem 2rem;
          min-width: 200px;
          border-radius: 2px;
          text-decoration: none;
          cursor: pointer;
          background: transparent;
          border: 1.5px solid var(--oasis-ty-cta-accent, var(--vibe-color-primary));
          color: var(--oasis-ty-cta-accent, var(--vibe-color-primary));
          box-shadow: none;
          transition: color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
        }
        .ty-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--oasis-ty-cta-accent, var(--vibe-color-primary));
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: 0;
        }
        .ty-btn-label {
          position: relative;
          z-index: 1;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        .ty-btn:hover {
          color: var(--oasis-ty-cta-on-accent, var(--vibe-color-text-on-primary));
          transform: translateY(-2px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--oasis-ty-cta-accent, var(--vibe-color-primary)) 30%, transparent);
        }
        .ty-btn:hover::before { opacity: 1; }
        .ty-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ThankYouContent',
  displayName: 'Thank You Content',
  tokens: {
    'thankyoucontent.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Thank You</p>',
    },
    'thankyoucontent.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue: '<p>Thank you for your interest! A team member will reach out to you soon to help you find your dream home.</p>',
    },
  },
  slotGroups: {
    'thankyoucontent.primary_cta': primaryCtaDef,
    'thankyoucontent.secondary_cta': secondaryCtaDef,
  },
};

(ThankYouContent as any).contentSchema = contentSchema;

export default ThankYouContent;
